import React, { useEffect, useMemo, useState } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useCampaign, useUsers } from 'hooks';
import { DocumentsList } from 'components';
import { getCampaignStatus, getChipColor, getFormattedDateTime, getFormattedDate } from 'utils';
import { Campaign, CampaignStatus, UserProfile } from 'types';
import { Autocomplete, Chip, Stack, TextField, Typography, Switch, Box } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { CampaignListActionsCell } from './CampaignListActionsCell';

const getColumns = (users: UserProfile[] | undefined = []): GridColDef<Campaign>[] => [
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 60,
    flex: 0.3,
  },
  {
    field: 'createdBy',
    headerName: 'Created By',
    minWidth: 100,
    flex: 0.25,
    sortable: false,
    renderCell: (params: GridRenderCellParams<Campaign>) => {
      const user = users.find((u) => u.id === params.value);
      if (!user) return null;
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
            {`${user.firstName} ${user.lastName}`}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.2 }}>
            {user.email}
          </Typography>
        </Box>
      );
    },
  },
  {
    field: 'startDate',
    headerName: 'Start date',
    minWidth: 60,
    flex: 0.1,
    valueFormatter: (value) => getFormattedDate(value),
    renderCell: (params: GridRenderCellParams<Campaign>) => (
      <div title={getFormattedDateTime(params.value) || ''}>{getFormattedDate(params.value)}</div>
    ),
  },
  {
    field: 'endDate',
    headerName: 'End date',
    minWidth: 60,
    flex: 0.1,
    valueFormatter: (value) => getFormattedDate(value),
    renderCell: (params: GridRenderCellParams<Campaign>) => (
      <div title={getFormattedDateTime(params.value) || ''}>{getFormattedDate(params.value)}</div>
    ),
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    minWidth: 60,
    flex: 0.08,
    valueFormatter: (value) => getFormattedDate(value),
    renderCell: (params: GridRenderCellParams<Campaign>) => (
      <div title={getFormattedDateTime(params.value) || ''}>{getFormattedDate(params.value)}</div>
    ),
  },
  {
    field: 'budget',
    headerName: 'Budget',
    minWidth: 60,
    flex: 0.1,
  },
  {
    field: 'invoicedAmount',
    headerName: 'Invoiced',
    minWidth: 60,
    flex: 0.1,
  },
  {
    field: 'paidAmount',
    headerName: 'Paid',
    minWidth: 60,
    flex: 0.1,
  },
  {
    field: 'state',
    headerName: 'Status',
    minWidth: 60,
    flex: 0.1,
    renderCell: (params: GridRenderCellParams<Campaign>) => {
      const { value, row } = params;
      const status = getCampaignStatus({
        state: value,
        startDate: row?.startDate || null,
        endDate: row?.endDate || null,
        isStopped: row.isStopped,
      });
      const { backgroundColor, color, borderColor } = getChipColor(status);

      return (
        <Chip
          label={status}
          sx={(theme) => ({
            width: 'fit-content',
            maxHeight: theme.spacing(6),
            fontWeight: 700,
            backgroundColor,
            color,
            border: `1px solid`,
            borderColor,
          })}
        />
      );
    },
  },
  {
    field: 'id',
    headerName: 'Actions',
    renderCell: (cellProps) => <CampaignListActionsCell cellProps={cellProps} />,
    sortable: false,
    filterable: false,
  },
];

export const CampaignList = () => {
  const { getAllCampaignsQuery } = useCampaign();
  const { usersListQuery } = useUsers();
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<CampaignStatus | null>(null);
  const [notInvoicedFilter, setNotInvoicedFilter] = useState(false);
  const [outstandingPaymentFilter, setOutstandingPaymentFilter] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const users = usersListQuery.data?.users;
  const columns = useMemo(() => getColumns(users), [users]);

  useEffect(() => {
    if (searchParams.has('userId')) {
      const userId = parseInt(searchParams.get('userId') ?? '');
      const user = users?.find((user) => user.id === userId);
      setSelectedUser(user?.id ?? null);
    }
  }, [searchParams, users]);

  const rows = useMemo(
    () =>
      getAllCampaignsQuery.data?.campaigns.filter((campaign) => {
        // User filter
        if (selectedUser !== null && campaign.createdBy !== selectedUser) {
          return false;
        }

        // Status filter
        if (selectedStatus !== null) {
          const campaignStatus = getCampaignStatus({
            state: campaign?.state,
            startDate: campaign?.startDate || null,
            endDate: campaign?.endDate || null,
            isStopped: campaign.isStopped,
          });

          if (campaignStatus !== selectedStatus) {
            return false;
          }
        }

        // Not Invoiced filter
        if (notInvoicedFilter && campaign.invoicedAmount > 0) {
          return false;
        }

        // Outstanding payment filter
        if (outstandingPaymentFilter && campaign.paidAmount >= campaign.invoicedAmount) {
          return false;
        }

        return true;
      }) ?? [],
    [getAllCampaignsQuery.data?.campaigns, selectedUser, selectedStatus, notInvoicedFilter, outstandingPaymentFilter]
  );

  const statusOptions = [
    { label: 'All Statuses', value: null },
    { label: 'Aktiv', value: CampaignStatus.active },
    { label: 'Entwurf', value: CampaignStatus.draft },
    { label: 'Beendet', value: CampaignStatus.over },
    { label: 'Bereit - In Prüfung', value: CampaignStatus.ready },
  ];

  const Filter = () => (
    <Stack direction="row" gap={3} width="100%" alignItems="center" flexWrap="wrap">
      <Autocomplete
        size="small"
        sx={{ minWidth: 200 }}
        value={users?.find((user) => user.id === selectedUser) ?? null}
        options={users ?? []}
        isOptionEqualToValue={(option, value) => option === value}
        renderInput={(params) => <TextField placeholder="Find user" variant="outlined" {...params} />}
        getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
        slotProps={{
          paper: {
            sx: { width: 'fit-content' },
          },
          popper: {
            sx: { width: 'fit-content' },
          },
        }}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.id}>
              <Stack spacing={1}>
                <Typography variant="body1">{`${option.firstName} ${option.lastName}`}</Typography>
                <Typography variant="body3">{option.email}</Typography>
              </Stack>
            </li>
          );
        }}
        onChange={(_, newValue, reason) => {
          if (reason === 'clear') {
            setSelectedUser(null);
            setSearchParams({});
            return;
          }
          setSearchParams({ userId: newValue?.id.toString() ?? '' });
        }}
      />

      <Autocomplete
        size="small"
        sx={{ minWidth: 180 }}
        value={statusOptions.find((option) => option.value === selectedStatus) ?? statusOptions[0]}
        options={statusOptions}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderInput={(params) => <TextField placeholder="Select status" variant="outlined" {...params} />}
        getOptionLabel={(option) => option.label}
        onChange={(_, newValue) => {
          setSelectedStatus(newValue?.value ?? null);
        }}
        disableClearable
      />

      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          Only Non-Invoiced
        </Typography>
        <Switch checked={notInvoicedFilter} onChange={(e) => setNotInvoicedFilter(e.target.checked)} size="small" />
      </Stack>

      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          Outstanding Payment
        </Typography>
        <Switch
          checked={outstandingPaymentFilter}
          onChange={(e) => setOutstandingPaymentFilter(e.target.checked)}
          size="small"
        />
      </Stack>
    </Stack>
  );

  return (
    <DocumentsList
      rows={rows}
      columns={columns}
      filter={<Filter />}
      isLoading={getAllCampaignsQuery.isLoading}
      storageKey={'allCampaigns'}
      rowHeight={45}
      initialSortModel={[
        {
          field: 'createdAt',
          sort: 'desc',
        },
      ]}
    />
  );
};
